import React, { useEffect, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Form,
    Modal,
    Row,
    Spinner,
    Table,
} from 'react-bootstrap';
import { FiCopy, FiDownload, FiEye, FiRefreshCw } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { getAdminSmsById, getAdminSmsList, getAdminSmsStatistics } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import { useTranslation } from '../../hooks/useTranslation';
import {
    SMS_MONITOR_PAGE_SIZES,
    SMS_MONITOR_STATUSES,
    formatSmsCost,
    formatSmsMonitorDate,
    getDefaultSmsMonitorDateRange,
    getSmsStatusBadgeVariant,
    buildSmsMonitorExcelRows,
    fetchAllSmsMonitorItems,
    truncateSmsSid,
} from '../../constants/smsMonitor';
import './SmsMonitorTab.css';

const defaultRange = getDefaultSmsMonitorDateRange();

const buildAppliedQuery = (overrides = {}) => ({
    dateFrom: defaultRange.date_from,
    dateTo: defaultRange.date_to,
    status: '',
    toNumber: '',
    fromNumber: '',
    page: 1,
    pageSize: 20,
    ...overrides,
});

const SmsMonitorTab = ({ isActive = true }) => {
    const { t } = useTranslation();
    const prefix = 'pages.adminTripsDaily.smsMonitor';

    const [dateFrom, setDateFrom] = useState(defaultRange.date_from);
    const [dateTo, setDateTo] = useState(defaultRange.date_to);
    const [status, setStatus] = useState('');
    const [toNumber, setToNumber] = useState('');
    const [fromNumber, setFromNumber] = useState('');
    const [appliedQuery, setAppliedQuery] = useState(() => buildAppliedQuery());
    const [refreshNonce, setRefreshNonce] = useState(0);

    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [statsError, setStatsError] = useState(null);

    const [items, setItems] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState(null);
    const [exportLoading, setExportLoading] = useState(false);
    const [exportError, setExportError] = useState(null);

    const [showDetail, setShowDetail] = useState(false);
    const [detailMessage, setDetailMessage] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);
    const [copiedField, setCopiedField] = useState(null);

    const resolveApiError = (error, fallback) => {
        if (error?.response?.status === 502) {
            return t(`${prefix}.errors.twilioUnavailable`);
        }
        if (error?.response?.status === 403) {
            return t(`${prefix}.errors.forbidden`);
        }
        return handleApiError(error, fallback);
    };

    useEffect(() => {
        if (!isActive) return;
        let cancelled = false;

        const fetchStats = async () => {
            setStatsLoading(true);
            setStatsError(null);
            try {
                const data = await getAdminSmsStatistics({
                    date_from: appliedQuery.dateFrom,
                    date_to: appliedQuery.dateTo,
                });
                if (!cancelled) setStats(data);
            } catch (error) {
                if (!cancelled) {
                    setStats(null);
                    setStatsError(resolveApiError(error, t(`${prefix}.errors.statsLoad`)));
                }
            } finally {
                if (!cancelled) setStatsLoading(false);
            }
        };

        const fetchList = async () => {
            setListLoading(true);
            setListError(null);
            try {
                const data = await getAdminSmsList({
                    page: appliedQuery.page,
                    page_size: appliedQuery.pageSize,
                    status: appliedQuery.status || undefined,
                    date_from: appliedQuery.dateFrom || undefined,
                    date_to: appliedQuery.dateTo || undefined,
                    to_number: appliedQuery.toNumber || undefined,
                    from_number: appliedQuery.fromNumber || undefined,
                });
                if (!cancelled) {
                    setItems(Array.isArray(data?.items) ? data.items : []);
                }
            } catch (error) {
                if (!cancelled) {
                    setItems([]);
                    setListError(resolveApiError(error, t(`${prefix}.errors.listLoad`)));
                }
            } finally {
                if (!cancelled) setListLoading(false);
            }
        };

        fetchStats();
        fetchList();

        return () => {
            cancelled = true;
        };
        // refreshNonce : rechargement manuel uniquement
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isActive,
        appliedQuery.dateFrom,
        appliedQuery.dateTo,
        appliedQuery.page,
        appliedQuery.pageSize,
        appliedQuery.status,
        appliedQuery.toNumber,
        appliedQuery.fromNumber,
        refreshNonce,
    ]);

    useEffect(() => {
        if (!isActive || listLoading || listError) return;
        if (appliedQuery.page > 1 && items.length === 0) {
            setAppliedQuery((prev) => ({
                ...prev,
                page: Math.max(1, prev.page - 1),
            }));
        }
    }, [isActive, listLoading, listError, appliedQuery.page, items.length]);

    const handleApplyFilters = (e) => {
        e?.preventDefault();
        setAppliedQuery({
            dateFrom,
            dateTo,
            status,
            toNumber,
            fromNumber,
            page: 1,
            pageSize: appliedQuery.pageSize,
        });
    };

    const handleResetFilters = () => {
        const range = getDefaultSmsMonitorDateRange();
        setDateFrom(range.date_from);
        setDateTo(range.date_to);
        setStatus('');
        setToNumber('');
        setFromNumber('');
        setAppliedQuery(buildAppliedQuery());
    };

    const handleRefresh = () => {
        setRefreshNonce((n) => n + 1);
    };

    const handleExportExcel = async () => {
        setExportLoading(true);
        setExportError(null);
        try {
            const allItems = await fetchAllSmsMonitorItems(appliedQuery, getAdminSmsList);
            const rows = buildSmsMonitorExcelRows(allItems);
            if (rows.length === 0) {
                setExportError(t(`${prefix}.exportNoData`));
                return;
            }
            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'SMS Twilio');
            const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '').slice(0, 14);
            XLSX.writeFile(workbook, `sms_monitor_export_${stamp}.xlsx`);
        } catch (error) {
            setExportError(resolveApiError(error, t(`${prefix}.exportError`)));
        } finally {
            setExportLoading(false);
        }
    };

    const hasNextPage = items.length === appliedQuery.pageSize;

    const handleCopy = async (text, fieldKey) => {
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(fieldKey);
            setTimeout(() => setCopiedField(null), 2000);
        } catch {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopiedField(fieldKey);
            setTimeout(() => setCopiedField(null), 2000);
        }
    };

    const openDetail = async (message) => {
        setShowDetail(true);
        setDetailMessage(message);
        setDetailError(null);
        setDetailLoading(true);
        try {
            const fresh = await getAdminSmsById(message.sid);
            setDetailMessage(fresh);
        } catch (error) {
            if (error?.response?.status === 404) {
                setDetailError(t(`${prefix}.errors.notFound`));
            } else {
                setDetailError(resolveApiError(error, t(`${prefix}.errors.detailLoad`)));
            }
        } finally {
            setDetailLoading(false);
        }
    };

    const closeDetail = () => {
        setShowDetail(false);
        setDetailMessage(null);
        setDetailError(null);
    };

    const renderStatCard = (label, value, variant = 'light', subValue = null) => (
        <Col xs={6} md={4} lg={2} className="mb-3">
            <Card className="sms-monitor-stat-card h-100">
                <Card.Body className="py-3">
                    <div className="text-muted small mb-1">{label}</div>
                    {statsLoading ? (
                        <Spinner animation="border" size="sm" />
                    ) : (
                        <>
                            <div className="fw-semibold fs-5">
                                {value ?? '—'}
                                {variant !== 'light' && value != null && (
                                    <Badge bg={variant} className="ms-2 align-text-top">
                                        {variant === 'success' ? '✓' : '!'}
                                    </Badge>
                                )}
                            </div>
                            {subValue && <div className="small text-muted">{subValue}</div>}
                        </>
                    )}
                </Card.Body>
            </Card>
        </Col>
    );

    return (
        <div className="sms-monitor-tab">
            <Card className="mb-3">
                <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <h6 className="mb-0">{t(`${prefix}.statsTitle`)}</h6>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={statsLoading || listLoading}
                    >
                        <FiRefreshCw className={`me-1 ${statsLoading || listLoading ? 'spinning' : ''}`} />
                        {t('common.refresh')}
                    </Button>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleApplyFilters} className="mb-3">
                        <Row className="g-2 align-items-end">
                            <Col md={3}>
                                <Form.Label className="small mb-1">{t(`${prefix}.dateFrom`)}</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                />
                            </Col>
                            <Col md={3}>
                                <Form.Label className="small mb-1">{t(`${prefix}.dateTo`)}</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                />
                            </Col>
                            <Col md="auto">
                                <Button type="submit" variant="primary" size="sm">
                                    {t(`${prefix}.applyDates`)}
                                </Button>
                            </Col>
                        </Row>
                    </Form>

                    {statsError && (
                        <Alert variant="danger" className="py-2 mb-3" dismissible onClose={() => setStatsError(null)}>
                            {statsError}
                        </Alert>
                    )}

                    <Row>
                        {renderStatCard(t(`${prefix}.kpi.total`), stats?.total)}
                        {renderStatCard(t(`${prefix}.kpi.delivered`), stats?.delivered, 'success')}
                        {renderStatCard(t(`${prefix}.kpi.failed`), stats?.failed, 'danger')}
                        {renderStatCard(t(`${prefix}.kpi.undelivered`), stats?.undelivered, 'warning')}
                        {renderStatCard(
                            t(`${prefix}.kpi.deliveryRate`),
                            stats?.delivery_rate != null ? `${stats.delivery_rate.toFixed(1)} %` : '—'
                        )}
                        {renderStatCard(
                            t(`${prefix}.kpi.totalCost`),
                            stats != null ? `${stats.total_cost?.toFixed(4) ?? '0'} ${stats.currency || ''}`.trim() : '—'
                        )}
                    </Row>
                    <p className="text-muted small mb-0">{t(`${prefix}.statsHint`)}</p>
                </Card.Body>
            </Card>

            <Card>
                <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <h6 className="mb-0">{t(`${prefix}.listTitle`)}</h6>
                    <Button
                        variant="outline-success"
                        size="sm"
                        onClick={handleExportExcel}
                        disabled={exportLoading || listLoading || statsLoading}
                    >
                        {exportLoading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                {t(`${prefix}.exportExcelLoading`)}
                            </>
                        ) : (
                            <>
                                <FiDownload className="me-1" />
                                {t(`${prefix}.exportExcel`)}
                            </>
                        )}
                    </Button>
                </Card.Header>
                <Card.Body>
                    {exportError && (
                        <Alert variant="danger" className="py-2 mb-3" dismissible onClose={() => setExportError(null)}>
                            {exportError}
                        </Alert>
                    )}
                    <Form onSubmit={handleApplyFilters} className="mb-3">
                        <Row className="g-2">
                            <Col md={2}>
                                <Form.Label className="small mb-1">{t(`${prefix}.status`)}</Form.Label>
                                <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                                    {SMS_MONITOR_STATUSES.map(({ value, labelKey }) => (
                                        <option key={value || 'all'} value={value}>
                                            {t(`${prefix}.statuses.${labelKey}`)}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small mb-1">{t(`${prefix}.toNumber`)}</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="+33600000000"
                                    value={toNumber}
                                    onChange={(e) => setToNumber(e.target.value)}
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small mb-1">{t(`${prefix}.fromNumber`)}</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="+14808450876"
                                    value={fromNumber}
                                    onChange={(e) => setFromNumber(e.target.value)}
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small mb-1">{t(`${prefix}.pageSize`)}</Form.Label>
                                <Form.Select
                                    value={appliedQuery.pageSize}
                                    onChange={(e) => {
                                        const nextSize = Number(e.target.value);
                                        setAppliedQuery((prev) => ({
                                            ...prev,
                                            pageSize: nextSize,
                                            page: 1,
                                        }));
                                    }}
                                >
                                    {SMS_MONITOR_PAGE_SIZES.map((size) => (
                                        <option key={size} value={size}>{size}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={4} className="d-flex align-items-end gap-2">
                                <Button type="submit" variant="primary" size="sm">
                                    {t('common.search')}
                                </Button>
                                <Button type="button" variant="outline-secondary" size="sm" onClick={handleResetFilters}>
                                    {t(`${prefix}.reset`)}
                                </Button>
                            </Col>
                        </Row>
                    </Form>

                    {listError && (
                        <Alert variant="danger" className="py-2 mb-3" dismissible onClose={() => setListError(null)}>
                            {listError}
                        </Alert>
                    )}

                    <div className="table-responsive sms-monitor-table-wrap">
                        <Table striped bordered hover size="sm" className="admin-table mb-0">
                            <thead>
                                <tr>
                                    <th>{t(`${prefix}.columns.sid`)}</th>
                                    <th>{t(`${prefix}.columns.date`)}</th>
                                    <th>{t(`${prefix}.columns.to`)}</th>
                                    <th>{t(`${prefix}.columns.from`)}</th>
                                    <th>{t(`${prefix}.columns.body`)}</th>
                                    <th>{t(`${prefix}.columns.status`)}</th>
                                    <th>{t(`${prefix}.columns.cost`)}</th>
                                    <th>{t(`${prefix}.columns.actions`)}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {listLoading ? (
                                    <tr>
                                        <td colSpan={8} className="text-center py-4">
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            {t('common.loading')}
                                        </td>
                                    </tr>
                                ) : items.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="text-center text-muted py-4">
                                            {t(`${prefix}.noData`)}
                                        </td>
                                    </tr>
                                ) : (
                                    items.map((item) => (
                                        <tr key={item.sid}>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-link btn-sm p-0 sms-sid-copy"
                                                    onClick={() => handleCopy(item.sid, item.sid)}
                                                    title={item.sid}
                                                >
                                                    <code>{truncateSmsSid(item.sid)}</code>
                                                    <FiCopy className="ms-1" />
                                                    {copiedField === item.sid && (
                                                        <span className="text-success small ms-1">✓</span>
                                                    )}
                                                </button>
                                            </td>
                                            <td className="text-nowrap small">
                                                {formatSmsMonitorDate(item.date_sent || item.date_created)}
                                            </td>
                                            <td className="small">{item.to || '—'}</td>
                                            <td className="small">{item.from_number || '—'}</td>
                                            <td className="small sms-body-cell">
                                                {item.body || '—'}
                                            </td>
                                            <td>
                                                <Badge bg={getSmsStatusBadgeVariant(item.status)}>
                                                    {item.status || '—'}
                                                </Badge>
                                            </td>
                                            <td className="small text-nowrap">
                                                {formatSmsCost(item.price, item.price_unit)}
                                            </td>
                                            <td>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => openDetail(item)}
                                                    title={t(`${prefix}.detail`)}
                                                >
                                                    <FiEye />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>

                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                        <span className="text-muted small">
                            {t(`${prefix}.pageIndicator`, { page: appliedQuery.page })}
                        </span>
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={appliedQuery.page <= 1 || listLoading}
                                onClick={() => setAppliedQuery((prev) => ({
                                    ...prev,
                                    page: Math.max(1, prev.page - 1),
                                }))}
                            >
                                {t(`${prefix}.prev`)}
                            </Button>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={!hasNextPage || listLoading}
                                onClick={() => setAppliedQuery((prev) => ({
                                    ...prev,
                                    page: prev.page + 1,
                                }))}
                            >
                                {t(`${prefix}.next`)}
                            </Button>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <Modal show={showDetail} onHide={closeDetail} size="lg" centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>{t(`${prefix}.detailTitle`)}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {detailLoading && (
                        <div className="text-center py-3">
                            <Spinner animation="border" size="sm" />
                        </div>
                    )}
                    {detailError && (
                        <Alert variant="danger">{detailError}</Alert>
                    )}
                    {detailMessage && !detailLoading && (
                        <div className="sms-detail-grid">
                            <DetailRow
                                label="SID"
                                value={detailMessage.sid}
                                onCopy={() => handleCopy(detailMessage.sid, 'detail-sid')}
                                copied={copiedField === 'detail-sid'}
                            />
                            <DetailRow label={t(`${prefix}.columns.to`)} value={detailMessage.to} />
                            <DetailRow label={t(`${prefix}.columns.from`)} value={detailMessage.from_number} />
                            <DetailRow
                                label={t(`${prefix}.columns.status`)}
                                value={
                                    <Badge bg={getSmsStatusBadgeVariant(detailMessage.status)}>
                                        {detailMessage.status}
                                    </Badge>
                                }
                            />
                            <DetailRow label={t(`${prefix}.direction`)} value={detailMessage.direction} />
                            <DetailRow
                                label={t(`${prefix}.columns.body`)}
                                value={<pre className="sms-detail-body mb-0">{detailMessage.body || '—'}</pre>}
                                full
                            />
                            <DetailRow label={t(`${prefix}.dateCreated`)} value={formatSmsMonitorDate(detailMessage.date_created)} />
                            <DetailRow label={t(`${prefix}.dateSent`)} value={formatSmsMonitorDate(detailMessage.date_sent)} />
                            <DetailRow label={t(`${prefix}.dateUpdated`)} value={formatSmsMonitorDate(detailMessage.date_updated)} />
                            <DetailRow
                                label={t(`${prefix}.columns.cost`)}
                                value={formatSmsCost(detailMessage.price, detailMessage.price_unit)}
                            />
                            <DetailRow label={t(`${prefix}.segments`)} value={detailMessage.num_segments ?? '—'} />
                            <DetailRow label={t(`${prefix}.media`)} value={detailMessage.num_media ?? '—'} />
                            {(detailMessage.error_code || detailMessage.error_message) && (
                                <DetailRow
                                    label={t(`${prefix}.error`)}
                                    value={
                                        <span className="text-danger">
                                            {detailMessage.error_code != null && `[${detailMessage.error_code}] `}
                                            {detailMessage.error_message || '—'}
                                        </span>
                                    }
                                    full
                                />
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeDetail}>
                        {t('common.close')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

const DetailRow = ({ label, value, onCopy, copied, full = false }) => (
    <div className={`sms-detail-row ${full ? 'sms-detail-row-full' : ''}`}>
        <div className="sms-detail-label text-muted small">{label}</div>
        <div className="sms-detail-value">
            {onCopy ? (
                <button type="button" className="btn btn-link btn-sm p-0 text-start" onClick={onCopy}>
                    <code>{value}</code>
                    <FiCopy className="ms-1" />
                    {copied && <span className="text-success small ms-1">✓</span>}
                </button>
            ) : (
                value ?? '—'
            )}
        </div>
    </div>
);

export default SmsMonitorTab;
